/**
 * Password policy – mirrors lambda/shared/passwordPolicy for the frontend.
 */

import type { PasswordPolicy } from '@lambda/shared/types';

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
};

export function arePasswordPoliciesEqual(
  a: Partial<PasswordPolicy> | null | undefined,
  b: Partial<PasswordPolicy> | null | undefined
): boolean {
  const na = normalizePasswordPolicy(a);
  const nb = normalizePasswordPolicy(b);
  return (
    na.minLength === nb.minLength &&
    na.requireUppercase === nb.requireUppercase &&
    na.requireLowercase === nb.requireLowercase &&
    na.requireNumber === nb.requireNumber &&
    na.requireSpecial === nb.requireSpecial
  );
}

export function normalizePasswordPolicy(partial?: Partial<PasswordPolicy> | null): PasswordPolicy {
  const minLength = partial?.minLength ?? DEFAULT_PASSWORD_POLICY.minLength;
  return {
    minLength: Math.min(128, Math.max(8, Math.round(minLength))),
    requireUppercase: partial?.requireUppercase ?? DEFAULT_PASSWORD_POLICY.requireUppercase,
    requireLowercase: partial?.requireLowercase ?? DEFAULT_PASSWORD_POLICY.requireLowercase,
    requireNumber: partial?.requireNumber ?? DEFAULT_PASSWORD_POLICY.requireNumber,
    requireSpecial: partial?.requireSpecial ?? DEFAULT_PASSWORD_POLICY.requireSpecial,
  };
}

export function getPasswordRequirementChecks(
  value: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): { id: string; label: string; met: boolean }[] {
  const p = normalizePasswordPolicy(policy);
  const checks: { id: string; label: string; met: boolean }[] = [
    { id: 'length', label: `${p.minLength} characters`, met: value.length >= p.minLength },
  ];
  if (p.requireUppercase) {
    checks.push({ id: 'upper', label: 'ABC', met: /[A-Z]/.test(value) });
  }
  if (p.requireLowercase) {
    checks.push({ id: 'lower', label: 'abc', met: /[a-z]/.test(value) });
  }
  if (p.requireNumber) {
    checks.push({ id: 'number', label: '123', met: /[0-9]/.test(value) });
  }
  if (p.requireSpecial) {
    checks.push({ id: 'special', label: '!@%', met: /[^A-Za-z0-9]/.test(value) });
  }
  return checks;
}

export function validatePasswordWithPolicy(
  value: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): { valid: boolean; message?: string } {
  const p = normalizePasswordPolicy(policy);
  if (value.length < p.minLength) {
    return { valid: false, message: `At least ${p.minLength} characters` };
  }
  if (p.requireUppercase && !/[A-Z]/.test(value)) {
    return { valid: false, message: 'One uppercase letter' };
  }
  if (p.requireLowercase && !/[a-z]/.test(value)) {
    return { valid: false, message: 'One lowercase letter' };
  }
  if (p.requireNumber && !/[0-9]/.test(value)) {
    return { valid: false, message: 'One number' };
  }
  if (p.requireSpecial && !/[^A-Za-z0-9]/.test(value)) {
    return { valid: false, message: 'One special character' };
  }
  return { valid: true };
}
